import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useUuiContext,
  INotification,
  useLazyDataSource,
  DataQueryFilter,
  LazyDataSourceApiRequestRange,
  PatchOrdering,
} from '@epam/uui-core';
import {
  Panel,
  Text,
  LinkButton,
  SuccessNotification,
  ErrorNotification,
} from '@epam/uui';
import {
  Box,
  Typography,
  useTheme,
  Skeleton,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { ADMIN } from '../../../../ListItems';
import CourierCard from '../CourierCard/CourierCard';
import CourierCreate from '../../../dialog/couriers/CourierDetailView';
import ConfirmMatchDialog from '../ConfirmMatchDialog/ConfirmMatchDialog';
import CourierDeactivateDialog from '../../../dialog/couriers/components/CourierDeactivateDialog/CourierDeactivateDialog';
import { Paths } from '../../../../router/paths';
import './CourierCardList.css';

interface ICourierCardList {
  sizeList: any;
  countryList: any;
  user: any;
  updatedCourier?: any;
  filter: any;
  onCourierUpdate: (courier: any) => void;
  cityList: any;
  courierId?: number;
}

let patch = new Map();

export default function CourierCardList({
  sizeList,
  cityList,
  countryList,
  user,
  updatedCourier,
  onCourierUpdate,
  filter,
  courierId,
}: ICourierCardList) {
  const svc = useUuiContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const [value, onValueChange] = useState<any>({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isAdmin = user.roles.includes(ADMIN);
  const courierToUpdate = isEmpty(updatedCourier) ? new Map() : updatedCourier;
  patch = new Map([...patch, ...courierToUpdate]);

  const showErrorNotification = (message: string) => {
    svc.uuiNotifications.show((props: INotification) => (
      <ErrorNotification {...props}>
        <Text size="36" fontSize="14">
          {message}
        </Text>
      </ErrorNotification>
    ));
    navigate(Paths.Courier);
  };

  useEffect(() => {
    const fetchCourier = async () => {
      if (
        courierId &&
        !isEmpty(countryList) &&
        !isEmpty(sizeList) &&
        !isEmpty(cityList)
      ) {
        try {
          const courier = await svc.api.courier.get(courierId);
          const isViewMode = courier.creator.id !== user.id && !isAdmin;
          if (courier.active || isAdmin) {
            svc.uuiModals.show((props) => (
              <CourierCreate
                countries={countryList}
                sizes={sizeList}
                courier={courier}
                isViewMode={isViewMode}
                onUpdate={onCourierUpdate}
                cityList={cityList}
                onClose={() => navigate(Paths.Courier)}
                {...props}
              />
            ));
          } else {
            showErrorNotification(
              "Unfortunately request wasn't found or already expired"
            );
          }
        } catch (error: Response | any) {
          showErrorNotification(error?.statusText || 'Something went wrong');
        }
      }
    };

    fetchCourier();
  }, [courierId, countryList, cityList, sizeList]);

  const dataSource = useLazyDataSource<any, string, DataQueryFilter<any>>(
    {
      api: async (request) => {
        const { sorting } = request;
        const { count, from } = request.range as LazyDataSourceApiRequestRange;
        const response = {
          items: await svc.api.table
            .getContent('courier/all', from, count, 'POST', {
              sort: {
                sort: sorting?.[0].field,
                sortDirection: sorting?.[0].direction?.toUpperCase(),
              },
              filter,
            })
            .then((r: any) => r.content)
            .catch((e: any) => console.log(e)),
        };

        return response;
      },
      getId: (p) => p.id,
      patch: isEmpty(filter) ? patch : new Map(),
      isDeleted: (item) => item.isDeleted ?? false,
      getNewItemPosition: () => PatchOrdering.BOTTOM,
      filter,
    },
    []
  );

  const view = dataSource.useView(value, onValueChange, {});

  // Track initial load completion
  useEffect(() => {
    const rows = view.getVisibleRows();
    if (rows && rows.length >= 0) {
      setIsInitialLoading(false);
    }
  }, [view]);

  const onCourierSubmit = (courierId: number) => {
    (async () => {
      svc.api.courier
        .match(courierId)
        .then(() => {
          svc.uuiNotifications
            .show((props: INotification) => (
              <SuccessNotification {...props}>
                <Text size="36" fontSize="14">
                  Match email was sent
                </Text>
              </SuccessNotification>
            ))
            .catch(() => null);
        })
        .catch((error: any) => {
          svc.uuiNotifications.show((props: INotification) => (
            <ErrorNotification {...props}>
              <Text size="36" fontSize="14">
                {error}
              </Text>
            </ErrorNotification>
          ));
        });
    })();
  };

  const handleCardClick = (courier: any) => {
    if (!courier) return;
    if (courier.active || isAdmin) {
      const isViewMode = courier.creator.id !== user.id && !isAdmin;
      svc.uuiModals.show((props) => (
        <CourierCreate
          countries={countryList}
          sizes={sizeList}
          courier={courier}
          isViewMode={isViewMode}
          onUpdate={onCourierUpdate}
          cityList={cityList}
          {...props}
        />
      ));
    }
  };

  const renderActions = (courier: any) => {
    if (!courier) return null;
    const { id, active, courierType, creator } = courier;
    if (!active) return null;

    if (user.id === creator.id) {
      return (
        <LinkButton
          caption="Complete"
          onClick={() =>
            svc.uuiModals.show((props) => (
              <CourierDeactivateDialog
                {...props}
                courier={courier}
                onDeactivate={() => props.success(true)}
                onUpdate={onCourierUpdate}
              />
            ))
          }
        />
      );
    }

    if (courierType === 'SENDER') {
      return (
        <LinkButton
          caption="I can help"
          onClick={() =>
            svc.uuiModals.show((props) => (
              <ConfirmMatchDialog
                {...props}
                onCourierSubmit={() => onCourierSubmit(id)}
              />
            ))
          }
        />
      );
    }

    if (courierType === 'COURIER') {
      return (
        <LinkButton
          caption="Transport my parcel"
          onClick={() =>
            svc.uuiModals.show((props) => (
              <ConfirmMatchDialog
                {...props}
                isCourierRequest
                onCourierSubmit={() => onCourierSubmit(id)}
              />
            ))
          }
        />
      );
    }

    return null;
  };

  const rows = view.getVisibleRows();
  const isLoading = isInitialLoading;

  return (
    <Panel background="surface-main" shadow cx="container">
      {/* Card List - Fluid Magazine Layout */}
      <Box
        className="courier-card-list"
        sx={{
          p: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
          maxWidth: '1800px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        {isLoading ? (
          // Loading skeleton - responsive grid
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(2, 1fr)',
                xl: 'repeat(3, 1fr)',
              },
              gap: { xs: 2, md: 3, lg: 4 },
            }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: `1px solid ${theme.palette.editorial.paperGray}`,
                  p: 3,
                  animation: 'skeletonPulse 1.5s ease-in-out infinite',
                  animationDelay: `${index * 100}ms`,
                }}
              >
                <Skeleton variant="text" width="40%" height={32} />
                <Skeleton
                  variant="text"
                  width="80%"
                  height={40}
                  sx={{ mt: 1 }}
                />
                <Skeleton
                  variant="text"
                  width="60%"
                  height={24}
                  sx={{ mt: 1 }}
                />
              </Box>
            ))}
          </Box>
        ) : rows.length === 0 ? (
          // Empty state - centered with editorial styling
          <Box
            sx={{
              textAlign: 'center',
              py: { xs: 8, md: 12 },
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                fontWeight: 700,
                letterSpacing: '-0.02em',
              }}
            >
              No courier requests found
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: '500px',
                lineHeight: 1.7,
              }}
            >
              Try adjusting your filters or create a new request to get started
            </Typography>
          </Box>
        ) : (
          // Courier cards - fluid magazine grid
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(2, 1fr)',
                xl: 'repeat(3, 1fr)',
              },
              gap: { xs: 2, md: 3, lg: 4 },
              // Masonry-like effect with auto rows
              gridAutoRows: 'auto',
            }}
          >
            {rows
              .filter((row) => row.value != null)
              .map((row, index) => {
                const courier = row.value;
                const isInactive = !courier.active;
                const isUserCourier = courier.creator?.id === user.id;

                return (
                  <Box
                    key={courier.id}
                    sx={{
                      // Staggered reveal animation
                      animation: 'cardReveal 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                      animationDelay: `${index * 80}ms`,
                      opacity: 0,
                      transform: 'translateY(20px)',
                    }}
                  >
                    <CourierCard
                      courier={courier}
                      index={index}
                      onClick={() => handleCardClick(courier)}
                      renderActions={() => renderActions(courier)}
                      isInactive={isInactive}
                      isUserCourier={isUserCourier}
                    />
                  </Box>
                );
              })}
          </Box>
        )}
      </Box>
    </Panel>
  );
}
